
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { PlanDetail, DiscountType, PaymentGatewayId, GatewayRule } from '../types';
import { CURRENCIES } from '../constants';
import { useSettings } from './SettingsContext';
import { api } from '../services/api';
import { processPostCheckoutSuccess } from '../services/postCheckoutService';
import { trackCheckoutInitiated, trackPurchase } from '../services/analyticsService';
import { notifyConnectedWebsite } from '../utils/checkoutUrlParams';
import { isGatewayConnected } from '../utils/gatewayUtils';
import { processRazorpaySubscription } from '../services/razorpayService';
import { processStripePayment } from '../services/stripeService';
import { processCashfreeSubscription } from '../services/cashfreeService';
import { processLemonSqueezySubscription } from '../services/lemonSqueezyService';
import { processPaddleSubscription } from '../services/paddleService';
import { processCheckoutComSubscription } from '../services/checkoutComService';
import { processChargebeeSubscription } from '../services/chargebeeService';

interface CheckoutModalProps {
  plan: PlanDetail;
  billingCycle: 'monthly' | 'quarterly' | 'annually';
  onClose: () => void;
  onSuccess: () => void;
  appliedCouponOverride?: { value: number, type: DiscountType } | null;
  customerDataOverride?: {
    name?: string;
    email?: string;
    phone?: string;
    gstin?: string;
    country?: string;
  };
  autoStart?: boolean;
}

const getCheckoutSearchParams = () => {
  let search = window.location.search;
  if (!search && window.location.hash.includes('?')) {
    search = window.location.hash.substring(window.location.hash.indexOf('?'));
  }
  return new URLSearchParams(search);
};

const readCheckoutUrlValue = (...keys: string[]): string => {
  const params = getCheckoutSearchParams();
  for (const key of keys) {
    const value = params.get(key)?.trim();
    if (value) return value;
  }
  return '';
};

const readCheckoutSessionId = () => readCheckoutUrlValue('checkout_session', 'session_id', 'session');

const readCheckoutSessionIdentity = async () => {
  const sessionId = readCheckoutSessionId();
  if (!sessionId) return null;

  const res = await fetch('/api/v1/checkout/session/' + encodeURIComponent(sessionId));
  if (!res.ok) return null;

  const session = await res.json();
  return {
    name: String(session?.customer?.name || session?.customerName || '').trim(),
    email: String(session?.customer?.email || session?.customerEmail || '').trim(),
    phone: String(session?.customer?.phone || session?.customerPhone || '').trim(),
    country: String(session?.customer?.country || session?.country || '').trim(),
    gstin: String(session?.customer?.gstin || session?.gstin || '').trim(),
  };
};

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'IN', name: 'India' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'OTH', name: 'Other Countries' },
];

const GATEWAY_METADATA: Record<PaymentGatewayId, { label: string; icon: React.ReactNode; color: string; hoverColor: string }> = {
  stripe: { 
    label: 'Stripe', 
    color: 'bg-[#635BFF]',
    hoverColor: 'hover:bg-[#534AD9]',
    icon: <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
  },
  razorpay: { 
    label: 'Razorpay', 
    color: 'bg-[#3395FF]',
    hoverColor: 'hover:bg-[#2176D3]',
    icon: <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17"/><path d="m7 21 1.6-4.5c.3-.8 1-1.5 1.8-1.5h7.6c.8 0 1.5.7 1.8 1.5L21 21"/></svg>
  },
  cashfree: { 
    label: 'Cashfree', 
    color: 'bg-[#00D481]',
    hoverColor: 'hover:bg-[#00B06B]',
    icon: <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10h18"/><rect width="20" height="14" x="2" y="5" rx="2"/></svg>
  },
  lemonsqueezy: { 
    label: 'Lemon', 
    color: 'bg-[#FFC233]',
    hoverColor: 'hover:bg-[#EBB12D]',
    icon: <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/></svg>
  },
  paddle: { 
    label: 'Paddle', 
    color: 'bg-[#1D1D1B]',
    hoverColor: 'hover:bg-black',
    icon: <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 10 10H12V2Z"/><path d="M12 2a10 10 0 0 1 10 10h-7.5l-3-3L12 2Z"/></svg>
  },
  checkoutcom: { 
    label: 'Checkout', 
    color: 'bg-[#000000]',
    hoverColor: 'hover:bg-slate-900',
    icon: <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12V4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8"/><rect width="20" height="8" x="2" y="14" rx="2"/></svg>
  },
  chargebee: { 
    label: 'Chargebee', 
    color: 'bg-[#5300eb]',
    hoverColor: 'hover:bg-[#4300bf]',
    icon: <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>
  }
};

const CheckoutModal: React.FC<CheckoutModalProps> = ({ 
  plan, 
  billingCycle, 
  onClose, 
  onSuccess, 
  appliedCouponOverride,
  customerDataOverride,
  autoStart = false 
}) => {
  const { settings } = useSettings();
  const [checkoutStep, setCheckoutStep] = useState<'review' | 'success'>('review');
  const [isProcessing, setIsProcessing] = useState(false);
  const autoStartedRef = useRef(false);
  const getResolvedBillingIdentity = () => ({
    name: billingName.trim() || customerDataOverride?.name?.trim() || readCheckoutUrlValue('name', 'customer_name'),
    email: billingEmail.trim() || customerDataOverride?.email?.trim() || readCheckoutUrlValue('email', 'customer_email'),
    phone: customerDataOverride?.phone?.trim() || readCheckoutUrlValue('phone', 'customer_phone'),
    country: country || customerDataOverride?.country?.trim() || readCheckoutUrlValue('country'),
    gstin: gstin.trim() || customerDataOverride?.gstin?.trim() || readCheckoutUrlValue('gstin'),
  });
  const [country, setCountry] = useState(() => customerDataOverride?.country?.trim() || readCheckoutUrlValue('country') || ((plan.currency === 'INR' || plan.currency === '₹') ? 'IN' : 'US'));
  const [gateway, setGateway] = useState<PaymentGatewayId | null>(null);
  const [workspaces, setWorkspaces] = useState(1);
  const [appliedCoupon, setAppliedCoupon] = useState<{ value: number, type: DiscountType } | null>(null);
  
  const [billingName, setBillingName] = useState(() => customerDataOverride?.name?.trim() || readCheckoutUrlValue('name', 'customer_name'));
  const [billingEmail, setBillingEmail] = useState(() => customerDataOverride?.email?.trim() || readCheckoutUrlValue('email', 'customer_email'));
  const [gstin, setGstin] = useState(() => customerDataOverride?.gstin?.trim() || readCheckoutUrlValue('gstin'));

  useEffect(() => {
    const nextName = customerDataOverride?.name?.trim() || readCheckoutUrlValue('name', 'customer_name');
    const nextEmail = customerDataOverride?.email?.trim() || readCheckoutUrlValue('email', 'customer_email');
    const nextGstin = customerDataOverride?.gstin?.trim() || readCheckoutUrlValue('gstin');
    const nextCountry = customerDataOverride?.country?.trim() || readCheckoutUrlValue('country');

    if (nextName) setBillingName(nextName);
    if (nextEmail) setBillingEmail(nextEmail);
    if (nextGstin) setGstin(nextGstin);
    if (nextCountry) setCountry(nextCountry);
  }, [customerDataOverride]);

  useEffect(() => {
    if (appliedCouponOverride !== undefined) {
      setAppliedCoupon(appliedCouponOverride);
    }
  }, [appliedCouponOverride]);

  const availableGateways = useMemo(() => {
    const allSupported: PaymentGatewayId[] = [
      'stripe',
      'razorpay',
      'cashfree',
      'lemonsqueezy',
      'paddle',
      'checkoutcom',
      'chargebee',
    ];

    return allSupported.filter((gwId) => {
      // 1. CRITICAL: Gateway MUST have API keys connected to SubScale
      if (!isGatewayConnected(gwId, settings.gatewayCredentials)) {
        return false;
      }

      // 2. Check routing rules
      const hasSpecificRule = settings.gatewayRules.some((r) => r.gateway === gwId);
      if (hasSpecificRule) {
        return settings.gatewayRules.some((rule: GatewayRule) => {
          if (!rule.isEnabled || rule.gateway !== gwId) return false;
          const matchesCurrency =
            rule.currencies.includes('*') ||
            rule.currencies.includes(plan.currency) ||
            (plan.currency === '₹' && rule.currencies.includes('INR'));
          const matchesCountry =
            rule.countries.includes('*') || rule.countries.includes(country);
          return matchesCurrency && matchesCountry;
        });
      }

      return true;
    });
  }, [settings.gatewayCredentials, settings.gatewayRules, plan.currency, country]);

  useEffect(() => {
    if (availableGateways.length > 0) {
      if (!gateway || !availableGateways.includes(gateway)) {
        setGateway(availableGateways[0]);
      }
    } else {
      setGateway(null);
    }
  }, [availableGateways, gateway]);

  const symbol = CURRENCIES.find(c => c.code === plan.currency)?.symbol || '₹';

  const getBasePrice = () => {
    let unitPrice = plan.price;
    let multiplier = 1;
    const quarterlyDiscount = plan.quarterlyDiscountPercentage ?? settings.checkoutConfig?.quarterlyDiscountPercentage ?? 0;
    const yearlyDiscount = plan.yearlyDiscountPercentage ?? settings.checkoutConfig?.yearlyDiscountPercentage ?? 0;

    if (billingCycle === 'quarterly') {
      unitPrice = quarterlyDiscount > 0 ? Math.round(plan.price * (1 - quarterlyDiscount / 100)) : plan.price;
      multiplier = 3;
    } else if (billingCycle === 'annually') {
      unitPrice = yearlyDiscount > 0 ? Math.round(plan.price * (1 - yearlyDiscount / 100)) : plan.price;
      multiplier = 12;
    }
    return unitPrice * multiplier * workspaces;
  };

  const getFinalDiscountedAmount = () => {
    const price = getBasePrice();
    if (!appliedCoupon) return price;
    if (appliedCoupon.type === DiscountType.PERCENTAGE) return Math.floor(price * (1 - appliedCoupon.value / 100));
    return Math.max(0, price - appliedCoupon.value);
  };

  const calculateFinalPrice = () => {
    const discounted = getFinalDiscountedAmount();
    const gst = (settings.isGstEnabled && country === 'IN') ? Math.floor(discounted * (settings.gstRate / 100)) : 0;
    return discounted + gst;
  };

  /**
   * Automated Post-Checkout Processing:
   * 1. Generates/Updates Customer
   * 2. Creates Active Subscription
   * 3. Issues Paid Invoice with Tax calculation
   * 4. Reconciles Abandonment Status to 'Recovered'
   */
  useEffect(() => {
    trackCheckoutInitiated(plan.name || plan.tier, calculateFinalPrice(), plan.currency || 'INR');
  }, []);

  const handleFinalSuccess = async (paymentId?: string) => {
    const discounted = getFinalDiscountedAmount();
    const gst = (settings.isGstEnabled && country === 'IN') ? Math.floor(discounted * (settings.gstRate / 100)) : 0;
    const finalAmount = discounted + gst;

    const billingIdentity = getResolvedBillingIdentity();

    trackPurchase(
      paymentId || `pay_${Date.now()}`,
      plan.name || plan.tier,
      finalAmount,
      plan.currency || 'INR',
      billingIdentity.email
    );

    notifyConnectedWebsite('SUBSCALE_CHECKOUT_SUCCESS', {
      planId: plan.id,
      tier: plan.tier,
      planName: plan.name,
      amount: finalAmount,
      currency: plan.currency || 'INR',
      paymentId: paymentId || `pay_${Date.now()}`,
      customerEmail: billingIdentity.email,
      customerName: billingIdentity.name,
    });

    try {
      await processPostCheckoutSuccess({
        plan,
        billingCycle,
        customerName: billingIdentity.name,
        customerEmail: billingIdentity.email,
        country: billingIdentity.country,
        gstin: billingIdentity.gstin,
        workspaces,
        finalAmount,
        taxableAmount: discounted,
        gstAmount: gst,
        gateway: gateway || 'razorpay',
        paymentId: paymentId || `pay_${Date.now()}`,
        appliedCouponCode: appliedCoupon ? 'DISCOUNT' : undefined,
      });
    } catch (err) {
      console.error("Post-checkout automated creation error:", err);
    }

    const sessionId = localStorage.getItem('ss_last_checkout_prospect_id');
    if (sessionId) {
      try {
        await api.abandonment.track({
          id: sessionId,
          status: 'Recovered'
        });
        localStorage.removeItem('ss_last_checkout_prospect_id');
        console.log("Session Reconciled: Prospect converted to Customer.");
      } catch (err) {
        console.error("Failed to update status to Recovered", err);
      }
    }

    const searchParams = new URLSearchParams(window.location.search);
    const returnUrl = searchParams.get('success_url') || searchParams.get('redirect_url') || searchParams.get('return_url');
    if (returnUrl) {
      try {
        const parsedUrl = new URL(returnUrl, window.location.origin);
        parsedUrl.searchParams.set('payment_id', paymentId || `pay_${Date.now()}`);
        parsedUrl.searchParams.set('plan', plan.tier || plan.name);
        parsedUrl.searchParams.set('status', 'success');
        setTimeout(() => {
          window.location.href = parsedUrl.toString();
        }, 1200);
      } catch (e) {
        console.warn('Invalid return URL', returnUrl);
      }
    }

    setCheckoutStep('success'); 
    setIsProcessing(false);
  };

  const handleConfirmSubscription = async () => {
    if (!gateway || isProcessing) return;
    let billingIdentity = getResolvedBillingIdentity();

    if (!billingIdentity.name || !billingIdentity.email) {
      try {
        const sessionIdentity = await readCheckoutSessionIdentity();
        if (sessionIdentity) {
          billingIdentity = {
            name: billingIdentity.name || sessionIdentity.name,
            email: billingIdentity.email || sessionIdentity.email,
            phone: billingIdentity.phone || sessionIdentity.phone,
            country: billingIdentity.country || sessionIdentity.country,
            gstin: billingIdentity.gstin || sessionIdentity.gstin,
          };
        }
      } catch (err) {
        console.warn('Failed to resolve checkout session identity', err);
      }
    }

    if ((!billingIdentity.name || !billingIdentity.email) && gateway !== 'razorpay') {
      alert('Please enter your billing name and email before continuing.');
      return;
    }

    if (gateway === 'razorpay') {
      billingIdentity = {
        ...billingIdentity,
        name: billingIdentity.name || 'Customer',
        email: billingIdentity.email || '',
        phone: billingIdentity.phone || '',
      };
    }

    setBillingName(billingIdentity.name);
    setBillingEmail(billingIdentity.email);
    if (billingIdentity.gstin) setGstin(billingIdentity.gstin);
    if (billingIdentity.country) setCountry(billingIdentity.country);
    setIsProcessing(true);
    const successCallback = (pid?: string) => handleFinalSuccess(pid);
    const cancelCallback = () => setIsProcessing(false);

    switch (gateway) {
      case 'stripe': 
        processStripePayment({
          mode: 'subscription',
          customerEmail: billingIdentity.email,
          lineItems: [{
            price_data: {
              currency: plan.currency === 'USD' || plan.currency === '$' ? 'usd' : 'inr',
              product_data: { name: `${plan.tier} Plan`, description: `Subscription for ${workspaces} workspace(s).` },
              unit_amount: Math.round(plan.price * 100),
              recurring: { interval: billingCycle === 'annually' ? 'year' : 'month', interval_count: billingCycle === 'quarterly' ? 3 : 1 },
            },
            quantity: workspaces,
          }],
          successUrl: window.location.origin + '?success=true',
          cancelUrl: window.location.origin + '?canceled=true',
          onSuccess: () => successCallback(`str_${Date.now()}`), 
          onCancel: cancelCallback 
        }); 
        break;
      case 'razorpay': {
        const params = new URLSearchParams(window.location.search || '');
        const storedCustomerId = params.get('customer_id') || params.get('customerId') || localStorage.getItem('portal_customer_id') || localStorage.getItem('subscale_customer_id') || '';
        api.checkout.createRazorpaySession({
          plan,
          billingCycle,
          customerId: storedCustomerId,
          customerName: billingIdentity.name,
          customerEmail: billingIdentity.email,
          customerContact: billingIdentity.phone,
        }).then((session) => {
          processRazorpaySubscription({
            keyId: session.keyId,
            orderId: session.orderId,
            subscriptionId: session.subscriptionId,
            amount: session.amount,
            currency: session.currency,
            name: settings.checkoutConfig.paymentDisplayName?.trim() || session.name || 'SubScale',
            description: session.description || plan.tier,
            userName: billingIdentity.name || session.prefill?.name || 'Customer',
            userEmail: billingIdentity.email || session.prefill?.email || 'customer@example.com',
            userContact: billingIdentity.phone || session.prefill?.contact || '9999999999',
            onSuccess: (res: any) => successCallback(res?.razorpay_payment_id || `rzp_${Date.now()}`),
            onDismiss: cancelCallback,
          });
        }).catch((err) => {
          console.error('Razorpay session error:', err);
          alert('Failed to initialize Razorpay checkout session.');
          cancelCallback();
        });
        break;
      }
      case 'chargebee': processChargebeeSubscription({ site: settings.gatewayCredentials.chargebee.site, publishableKey: settings.gatewayCredentials.chargebee.publishableKey, planId: plan.id, onSuccess: () => successCallback(`cb_${Date.now()}`), onClose: cancelCallback }); break;
      case 'cashfree': 
        processCashfreeSubscription({ 
          paymentSessionId: 'session_mock_123', 
          onSuccess: (result) => {
            console.log('Cashfree success:', result);
            successCallback(`cf_${Date.now()}`);
          }, 
          onFailure: (error) => {
            console.error('Cashfree failure:', error);
            cancelCallback();
          } 
        }); 
        break;
      case 'paddle':
        processPaddleSubscription({
          priceId: 'pri_mock_paddle_id',
          customerEmail: billingIdentity.email,
          onSuccess: (data) => {
            console.log('Paddle success:', data);
            successCallback(`pad_${Date.now()}`);
          },
          onClose: cancelCallback
        });
        break;
      default: setTimeout(() => successCallback(`txn_${Date.now()}`), 1500);
    }
  };

  useEffect(() => {
    if (!autoStart || autoStartedRef.current || checkoutStep !== 'review' || !gateway || availableGateways.length === 0) return;
    autoStartedRef.current = true;
    handleConfirmSubscription();
  }, [autoStart, checkoutStep, gateway, availableGateways.length]);

  if (checkoutStep === 'success') {
    return (
      <div className="bg-white rounded-[2.5rem] p-12 text-center space-y-8 animate-in zoom-in-95">
        <div className="w-24 h-24 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h2 className="text-3xl font-black text-slate-900">Success! Welcome aboard.</h2>
        <p className="text-slate-500">Your checkout has been successfully converted to a customer account. The recovery timer has been deactivated.</p>
        <button onClick={onSuccess} className="w-full py-5 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl">Launch Dashboard</button>
      </div>
    );
  }

  const finalPrice = calculateFinalPrice();
  const basePrice = getBasePrice();
  const couponSaving = basePrice - getFinalDiscountedAmount();

  return (
    <div className="space-y-10 animate-in fade-in duration-500 w-full">
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Payment Region</label>
          <div className="relative">
            <select 
              className="w-full pl-10 pr-6 py-4 bg-slate-50 border border-slate-200 rounded-[1.5rem] outline-none focus:ring-8 focus:border-indigo-600 transition-all font-bold text-sm appearance-none"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" x2="22" y1="12" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Units</label>
          <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-[1.5rem] p-2 h-[58px]">
             <button onClick={() => setWorkspaces(Math.max(1, workspaces - 1))} className="w-10 h-full rounded-xl hover:bg-white text-slate-400 hover:text-indigo-600 transition-all font-black text-lg">-</button>
             <span className="flex-1 text-center font-black text-sm">{workspaces}</span>
             <button onClick={() => setWorkspaces(workspaces + 1)} className="w-10 h-full rounded-xl hover:bg-white text-slate-400 hover:text-indigo-600 transition-all font-black text-lg">+</button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between ml-1">
          <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block">Payment Method</label>
          <span className="text-[10px] font-bold text-slate-400">
            {availableGateways.length > 0
              ? `${availableGateways.length} Connected Gateway${availableGateways.length > 1 ? 's' : ''}`
              : '0 Gateways Connected'}
          </span>
        </div>

        {availableGateways.length === 0 ? (
          <div className="p-6 bg-amber-50/90 border border-amber-200/90 rounded-[2rem] text-center space-y-2.5 animate-in fade-in">
            <div className="w-10 h-10 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" x2="12" y1="8" y2="12" />
                <line x1="12" x2="12.01" y1="16" y2="16" />
              </svg>
            </div>
            <h4 className="text-sm font-black text-amber-900">No Payment Gateways Connected</h4>
            <p className="text-xs text-amber-700 max-w-sm mx-auto leading-relaxed">
              Only payment gateways with connected API keys are presented to customers. Please configure API keys in <strong>Settings &gt; Gateway Credentials</strong> to accept transactions.
            </p>
          </div>
        ) : (
          <div className={`grid ${availableGateways.length === 1 ? 'grid-cols-1' : availableGateways.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'} gap-4`}>
            {availableGateways.map((id) => {
              const meta = GATEWAY_METADATA[id];
              const isSelected = gateway === id;
              return (
                <button 
                  key={id} 
                  type="button"
                  onClick={() => setGateway(id)} 
                  className={`flex flex-col items-center justify-center p-6 rounded-[2rem] border-2 transition-all gap-3 relative h-32 ${
                    isSelected ? 'border-indigo-600 bg-indigo-50/50 ring-8 ring-indigo-50 shadow-xl' : 'border-slate-100 hover:border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                >
                  <div className={`p-3 rounded-2xl transition-all ${isSelected ? meta.color + ' text-white scale-110' : 'bg-slate-100 text-slate-400'}`}>{meta.icon}</div>
                  <span className={`text-[10px] font-black uppercase tracking-[0.1em] ${isSelected ? 'text-indigo-600' : 'text-slate-500'}`}>{meta.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-200/50 relative overflow-hidden group">
        <div className="space-y-4 relative z-10">
           <div className="flex justify-between items-center text-xs font-bold text-slate-500">
              <span className="uppercase tracking-[0.2em]">Base Fee x {workspaces}</span>
              <span className="font-black text-slate-700">{symbol}{basePrice.toLocaleString()}</span>
           </div>
           {couponSaving > 0 && (
              <div className="flex justify-between items-center text-xs font-black text-emerald-600">
                 <span className="uppercase tracking-[0.2em]">Discount Applied</span>
                 <span>-{symbol}{couponSaving.toLocaleString()}</span>
              </div>
           )}
           <div className="pt-6 border-t border-slate-200 flex justify-between items-center">
              <span className="text-sm font-black text-slate-900 uppercase tracking-[0.3em]">Total Due</span>
              <span className="text-4xl font-black text-slate-900 tracking-tighter">{symbol}{finalPrice.toLocaleString()}</span>
           </div>
        </div>
      </div>

      <button 
        disabled={!gateway || isProcessing || availableGateways.length === 0} 
        onClick={handleConfirmSubscription} 
        className={`w-full py-6 rounded-[2rem] font-black text-xs uppercase tracking-[0.25em] shadow-[0_20px_50px_rgba(0,0,0,0.15)] transition-all flex items-center justify-center gap-4 relative overflow-hidden group ${
          gateway ? GATEWAY_METADATA[gateway].color + ' hover:brightness-110' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
        } text-white active:scale-[0.98]`}
      >
        <span className="relative z-10">
          {!gateway || availableGateways.length === 0
            ? 'No Gateway Connected'
            : isProcessing
            ? 'Processing Transaction...'
            : 'Checkout Securely'}
        </span>
      </button>
    </div>
  );
};

export default CheckoutModal;
